import { useState } from 'react';
import type { Playlist, PlaylistSummary, Track } from '@blackblack/shared';
import { api, errorMessage } from '../../lib/api';
import { invalidate, useQuery } from '../../lib/query';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/toast';
import { Icon } from '../../components/ui/Icon';
import { Button, Skeleton, TextField } from '../../components/ui/primitives';
import { Sheet } from '../../components/ui/Sheet';
import { Cover } from './Cover';
import styles from './music.module.css';

export function AddToPlaylistSheet({ track, onClose }: { track: Track; onClose: () => void }) {
  const { me } = useAuth();
  const toast = useToast();
  const mine = useQuery<PlaylistSummary[]>(me ? `playlists:user:${me.id}` : null, () => api.get(`/users/${me!.id}/playlists`));
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const add = async (playlistId: string, name: string) => {
    setBusy(playlistId);
    try {
      await api.post<Playlist>(`/playlists/${playlistId}/tracks`, { trackId: track.id });
      invalidate(`playlists:`);
      invalidate(`playlist:${playlistId}`);
      toast(`Toegevoegd aan ${name}`, { tone: 'success' });
      onClose();
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const createAndAdd = async () => {
    if (!title.trim()) return;
    setBusy('new');
    try {
      const p = await api.post<Playlist>('/playlists', { title: title.trim() });
      await add(p.id, p.title);
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
      setBusy(null);
    }
  };

  return (
    <Sheet open onClose={onClose} title="Toevoegen aan playlist">
      <div className={styles.sheetPad}>
        {creating ? (
          <div className={styles.newPlaylist}>
            <TextField label="Naam van de playlist" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} autoFocus placeholder="Mijn playlist" />
            <Button block loading={busy === 'new'} disabled={!title.trim()} onClick={createAndAdd}>
              Maken en toevoegen
            </Button>
          </div>
        ) : (
          <button className={styles.listRow} onClick={() => setCreating(true)}>
            <span className={styles.newIcon}>
              <Icon name="plus" size={22} />
            </span>
            <span className={styles.rowTitle}>Nieuwe playlist</span>
          </button>
        )}
        {mine.loading &&
          [0, 1, 2].map((i) => (
            <div key={i} className={styles.listRow}>
              <Skeleton w={48} h={48} />
              <Skeleton w={140} h={14} />
            </div>
          ))}
        {mine.data?.map((p) => (
          <button key={p.id} className={styles.listRow} onClick={() => add(p.id, p.title)} disabled={!!busy}>
            <Cover src={p.coverUrl} size={48} />
            <span className={styles.rowText}>
              <span className={styles.rowTitle}>{p.title}</span>
              <span className={styles.rowSub}>
                {p.trackCount} nummers {!p.isPublic && '· privé'}
              </span>
            </span>
            {busy === p.id && <Icon name="check" size={18} />}
          </button>
        ))}
      </div>
    </Sheet>
  );
}
